import { Box, FormControl } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../../redux/hook'
import { TemplatePermissionMode } from '../../../constants/constants'
import { useTranslation } from 'react-i18next'
import SearchInAPIAndMultiSelectInput, {
    SelectItem,
} from '../../Common/SearchInAPIAndMultiSelectInput'
import { fetchRecordsUIDsByTemplateId } from '../../../services/entityInstanceServices'

interface ColumnEntityTypeFilterProps {
    id: string
    label: string
    value: any
    onChange: any
    templateId: string
    orgId: string
}

const ColumnEntityTypeFilter = (
    props: ColumnEntityTypeFilterProps,
): JSX.Element => {
    const { id, label, value, onChange, templateId, orgId } = props
    const { t } = useTranslation()
    const org = useAppSelector((state) => state.org.org)

    const [selectedValues, setSelectedValues] = useState<any[]>([])

    const [recordsList, setRecordsList] = useState<SelectItem[]>([])

    const [searchInput, setSearchInput] = useState<string>('')

    const [searchText, setSearchText] = useState<string>('')

    const [pageNumber, setPageNumber] = useState<number>(1)

    const [hasMore, setHasMore] = useState<boolean>(false)

    const [isFetching, setIsFetching] = useState(false)

    const requestIdRef = useRef(0)
    const searchInputRef = useRef('')

    useEffect(() => {
        setSelectedValues(value)
    }, [value])

    useEffect(() => {
        searchInputRef.current = searchInput
    }, [searchInput])

    useEffect(() => {
        const debounceTimer = window.setTimeout(() => {
            setPageNumber(1)
            setSearchText(searchInput)
        }, 300)

        return () => window.clearTimeout(debounceTimer)
    }, [searchInput])

    useEffect(() => {
        if (templateId && orgId) {
            const requestId = ++requestIdRef.current
            let isActive = true
            setIsFetching(true)

            const getRecords = async () => {
                const queryParams = {
                    start: pageNumber,
                    pageSize: 30,
                    searchText: searchText?.trim(),
                    filterBy: 'ACTIVE',
                    shipmentStatusFilter: '',
                    includeUseOnly: true,
                    includeCommonOrg: true,
                    entitySearch: true,
                }
                try {
                    const records = await fetchRecordsUIDsByTemplateId(
                        orgId || org?.id || '',
                        templateId,
                        queryParams,
                    )
                    if (!isActive || requestId !== requestIdRef.current) return

                    const updatedRecords = records?.data?.map((i: any) => {
                        return {
                            ...i,
                            hasItemEdit:
                                i.action === TemplatePermissionMode.WRITE ||
                                i.action === TemplatePermissionMode.READ_WRITE,
                        }
                    })
                    setRecordsList((prev) =>
                        pageNumber === 1
                            ? updatedRecords
                            : [...prev, ...updatedRecords],
                    )
                    setHasMore(updatedRecords?.length === 30)
                } finally {
                    if (isActive && requestId === requestIdRef.current) {
                        setIsFetching(false)
                    }
                }
            }
            getRecords()

            return () => {
                isActive = false
            }
        }
        return undefined
    }, [templateId, orgId, pageNumber, searchText, org?.id])

    const onSelectRecords = (id: string, items: any) => {
        onChangeRecord(id, items)
    }
    const onSearch = (id: string, text: any) => {
        if (text === searchInputRef.current) return

        requestIdRef.current += 1
        setIsFetching(true)
        searchInputRef.current = text
        setSearchInput(text)
    }

    const handleFetchNextPage = () => {
        if (!isFetching && hasMore && searchInput === searchText) {
            setPageNumber((prev) => prev + 1)
        }
    }

    const onChangeRecord = (id: string, selectedList: any[]) => {
        setSelectedValues(selectedList)
        onChange(id, selectedList)
    }

    const isLoadingSearchResults =
        isFetching && (pageNumber === 1 || searchInput !== searchText)

    return (
        <Box>
            <Box>
                <FormControl fullWidth size="small">
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: `100%`,
                            }}
                        >
                            <SearchInAPIAndMultiSelectInput
                                id={id}
                                label={label}
                                placeholder={t('selectLabel', { label })}
                                value={selectedValues}
                                onSelect={onSelectRecords}
                                list={
                                    isLoadingSearchResults
                                        ? []
                                        : recordsList || []
                                }
                                hasAddNew={false}
                                onSearch={onSearch}
                                onFetchNextPage={handleFetchNextPage}
                                hasMore={hasMore}
                                isFetching={isFetching}
                                itemId="entityId"
                                itemName="uid"
                                autoFocusOnMount={true}
                                stopKeyboardPropagation={true}
                            />
                        </Box>
                    </Box>
                </FormControl>
            </Box>
        </Box>
    )
}

export default ColumnEntityTypeFilter
